import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, Hourglass, Mail } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import api from "../store/api";
import { useCart } from "../store/CartContext";

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();

  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const pollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchOrder = async () => {
      try {
        const r = await api.get(`/orders/${orderId}`);
        if (alive) setOrder(r.data);
      } catch {}
    };
    fetchOrder();

    if (sessionId) {
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          const s = await api.get(`/payments/status/${sessionId}`);
          await fetchOrder();
          if (s.data.payment_status === "paid") {
            if (alive) {
              setPolling(false);
              clear();
            }
            return;
          }
        } catch {}
        if (attempts < 18 && alive) {
          pollRef.current = setTimeout(tick, 2500);
        } else if (alive) {
          setPolling(false);
        }
      };
      tick();
    }
    return () => {
      alive = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [orderId, sessionId, clear]);

  if (!order) {
    return (
      <DahliasShell>
        <div className="max-w-3xl mx-auto px-5 py-20 text-center">Loading your order…</div>
      </DahliasShell>
    );
  }

  const isPaid = order.payment_status === "paid";
  const isBank = order.payment_method === "bank-transfer";

  return (
    <DahliasShell>
      <section data-testid="order-success" className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 wobble-blob bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] flex items-center justify-center">
            {isPaid ? <Check className="w-7 h-7" /> : <Hourglass className="w-7 h-7" />}
          </div>
          <span className="block mt-5 font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
            ✦ {isPaid ? "PAID" : isBank ? "AWAITING BANK TRANSFER" : "AWAITING PAYMENT"} ✦
          </span>
          <h1 data-testid="order-status" className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-2 leading-[1.05]">
            {isPaid ? <>Thank <span className="font-display-italic text-[var(--dahlias-sage-deep)]">you!</span></> : isBank ? "Order received" : (polling ? "Confirming payment…" : "Payment pending")}
          </h1>
          <p className="mt-4 text-lg text-[var(--dahlias-ink)]/75">
            Order <span className="font-display text-[var(--dahlias-sage-deep)]" data-testid="order-number">{order.order_number}</span>
          </p>
          <p className="mt-2 text-base text-[var(--dahlias-ink)]/65 inline-flex items-center gap-2">
            <Mail className="w-4 h-4" /> A confirmation has been sent to {order.email}
          </p>
        </div>

        <div className="mt-10 bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-6 md:p-8">
          <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Items</h2>
          <div className="mt-4 divide-y divide-[var(--dahlias-sage)]/15">
            {order.items.map((it, i) => (
              <div key={i} className="py-3 flex items-start justify-between gap-4 text-base">
                <div>
                  <div className="font-medium">{it.name}</div>
                  {it.variant_label && <div className="text-sm text-[var(--dahlias-ink)]/65">{it.variant_label}</div>}
                  <div className="text-sm text-[var(--dahlias-ink)]/60 mt-0.5">× {it.qty}</div>
                </div>
                <div className="text-[var(--dahlias-ink)]">NZ${it.line_total.toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[var(--dahlias-sage)]/20 space-y-2 text-base">
            <div className="flex justify-between"><span>Subtotal</span><span>NZ${order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{order.shipping_cost === 0 ? "FREE" : `NZ$${order.shipping_cost.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-display text-2xl text-[var(--dahlias-forest)] pt-3 border-t border-[var(--dahlias-sage)]/20">
              <span>Total</span><span>NZ${order.total.toFixed(2)}</span>
            </div>
          </div>

          {isBank && !isPaid && (
            <div className="mt-6 bg-[var(--dahlias-cream)] border-2 border-[var(--dahlias-clay)]/40 wobble-b p-5">
              <p className="font-display text-lg text-[var(--dahlias-forest)]">Bank transfer details</p>
              <p className="mt-2 text-base text-[var(--dahlias-ink)]/80 leading-relaxed">
                Please transfer <strong>NZ${order.total.toFixed(2)}</strong> to Heritage Dahlias.<br />
                We'll email the account number and use <strong>{order.order_number}</strong> as your reference.
              </p>
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <Link to="/shop" className="inline-block text-[var(--dahlias-sage-deep)] hover:underline">← Back to the shop</Link>
        </div>
      </section>
    </DahliasShell>
  );
}
